// =============================================================================
// Le tableau du temps 1 « Comprendre le résultat » (revue du 08/10/2026, R12).
//
// Ce que le client doit vivre : d'un coup d'œil, plage par plage, la puissance
// souscrite aujourd'hui, la pointe réellement mesurée et la puissance proposée,
// puis la formule avant → après. Le PDF du moteur adopte les mêmes colonnes.
//
// Règles :
// - une pointe absente reste absente (`null`), jamais 0 ;
// - « Proposé » ne lit `meilleure` que sous une structure RECOMMANDÉE : sous
//   « déjà optimale », une option sous le seuil ou un surcoût, rien n'est
//   proposé de changer, et la colonne n'est pas affichée (l'option chiffrée
//   garde son bloc à part, DEV-2183) ;
// - « Vos puissances » (étude saisie, D2 de la série UX du 15/10/2026) lit les
//   puissances SAISIES, jamais l'actuelle : voir `structureSaisie` ;
// - C5 : une seule ligne, la puissance unique face à la pointe la plus haute.
//
// Unité de la pointe : le moteur sert `pointes_mesurees_kw`, le maximum de la
// puissance active mesurée (kW), et la compare telle quelle aux puissances
// souscrites, sans conversion (aucun cos φ n'est servi : règle écrite en tête
// de `courtage/modules/optimturpe/bornes_recommandation.py`). Son contrôle du
// dénivelé (`controler_denivele_actuel`) sert d'ailleurs une seule `unite` par
// ligne, pour la souscrite comme pour la pointe. Le tableau suit la même
// convention que la Preuve : toute la ligne s'affiche dans l'unité de la
// puissance du segment (`getUnitePuissance` : kVA en C5 et C4, kW en C2).
// =============================================================================
import { formateurFr } from "@modules/courtage/utils/optim-turpe-v2.nombre"
import {
    PLAGE_ORDER,
    estC5,
    getDrivingPlage,
    getFtaChange,
    getUnitePuissance,
} from "@modules/courtage/utils/optim-turpe-v2.helpers"
import {
    lirePointesMesurees,
    lireStatutStructure,
    montantC5,
    reserveTitreC5,
} from "@modules/courtage/utils/optim-turpe-v2.recommandation"
import { relireValeurServie } from "@modules/courtage/utils/optim-turpe-v2.marge"
import { LIBELLE_CLASSE_HORAIRE } from "@modules/courtage/utils/optim-turpe-v2.libelles"
import { lireRaccordement } from "@modules/courtage/utils/optim-turpe-v2.bornes"
import {
    libelleClasseHoraire,
    libelleFormuleTarifaireSansCode,
} from "@modules/courtage/utils/optim-turpe-v2.libelles"
import { lireChiffre } from "@modules/courtage/utils/optim-turpe-v2.chiffre"
import { montantDeTete } from "@modules/courtage/utils/optim-turpe-v2.titre"
import type {
    OptimTurpeV2Etude,
    OptimTurpeV2PlageKey,
    OptimTurpeV2StructureTarifaire,
} from "@modules/courtage/types/optim-turpe-v2"

/** Une ligne du tableau : les trois valeurs, chacune `null` quand elle manque. */
export type LigneTableauPlages = {
    /** Code de la plage (`HCB`…) ou `unique` en C5. */
    cle: string
    /** La plage en clair, majuscule initiale. */
    libelle: string
    aujourdhui: number | null
    pointe: number | null
    propose: number | null
}

/** Libellé de l'unique ligne d'un C5 : une seule puissance, à toute heure. */
export const LIBELLE_LIGNE_UNIQUE = "Toutes les heures"

function fini(v: unknown): number | null {
    return typeof v === "number" && Number.isFinite(v) ? v : null
}

function majuscule(texte: string): string {
    return texte ? texte.charAt(0).toUpperCase() + texte.slice(1) : texte
}

/** La structure proposée n'est lue que sous une structure recommandée. */
function proposeeLue(etude: OptimTurpeV2Etude): boolean {
    return lireStatutStructure(etude) === "recommandee"
}

/**
 * Le nom de la troisième colonne (vague wording du 14/10/2026, Q5, Q21, R5,
 * A7) : « Vos puissances » sur une étude saisie ; « Recommandé » quand
 * l'étude recommande de changer ; AUCUNE colonne sinon. Sous une économie non
 * chiffrée (défaut bloquant, comme au PDF), sous « Rien à changer » ou un
 * surcoût, elle ne faisait que recopier la puissance d'aujourd'hui ; l'option
 * chiffrée garde son bloc à part (DEV-2183).
 */
type Puissances = NonNullable<
    OptimTurpeV2StructureTarifaire["puissance_souscrite_soutirage"]
>

/** Les puissances portent-elles au moins une valeur lisible ? */
function puissancesLisibles(p: Puissances | null | undefined): boolean {
    if (!p) return false
    if (fini(p.puissance) !== null) return true
    return Object.values(p.denivele ?? {}).some((v) => fini(v) !== null)
}

/**
 * Les puissances SAISIES d'une étude « Vos puissances » (D2 de la série UX du
 * 15/10/2026), jamais celles du contrat actuel. Ordre de lecture, établi sur
 * une étude saisie réelle (lecture seule de la base, 15/10/2026) :
 * 1. `meilleure` : la structure chiffrée, qui porte la saisie (HPH 60 lu sur
 *    l'étude saisie, identique à `inputs.optimisation.cible`) ;
 * 2. `inputs.optimisation.cible` : là où la saisie est envoyée au moteur ;
 * 3. `conduite.cible_saisie` : prévu par le contrat (§5), non servi sur
 *    l'étude réelle relue, gardé en dernier repli.
 * `null` quand aucune n'est lisible : la colonne ne s'affiche pas.
 */
export function structureSaisie(etude: OptimTurpeV2Etude): Puissances | null {
    const candidates = [
        etude.meilleure?.structure_tarifaire?.puissance_souscrite_soutirage,
        etude.inputs?.optimisation?.cible?.puissance_souscrite_soutirage,
        etude.conduite?.cible_saisie?.puissance_souscrite_soutirage,
    ]
    return candidates.find(puissancesLisibles) ?? null
}

/** Le nom de la colonne d'un scénario affiché qui n'est pas le recommandé. */
export const COLONNE_OPTION_CHIFFREE = "Option chiffrée"

/**
 * Ce que la page sait et que l'étude seule ne dit pas. `optionChiffree` :
 * le scénario affiché n'est PAS le scénario recommandé (« Couvrir » retenu
 * quand le conseil établi est « Assumer », ou l'inverse).
 */
export type OptionsDuGraphique = { optionChiffree?: boolean }

export function colonneProposee(
    etude: OptimTurpeV2Etude,
    options: OptionsDuGraphique = {}
): string | null {
    const chiffre = lireChiffre(etude)
    // Les valeurs saisies restent affichées, même sans montant (« vos
    // puissances restent affichées ») : ce ne sont pas une recommandation.
    // Sans puissance saisie lisible, aucune colonne (D2) : jamais l'actuelle.
    if (chiffre.estScenarioLibre)
        return structureSaisie(etude) ? "Vos puissances" : null
    if (chiffre.montantServi === false || chiffre.etat === "montant_non_servi")
        return null
    // Revue 2 en production (I24, I61, DEV-2404) : le scénario retenu qui
    // n'est pas le recommandé garde sa colonne, nommée « Option chiffrée »,
    // avec ses puissances ; il n'est jamais dit « Recommandé ».
    if (options.optionChiffree)
        return puissancesLisibles(
            etude.meilleure?.structure_tarifaire?.puissance_souscrite_soutirage
        )
            ? COLONNE_OPTION_CHIFFREE
            : null
    return proposeeLue(etude) ? "Recommandé" : null
}

/**
 * Les postes du compteur (R19, L17, QM12) : un C4 n'a pas de poste de pointe,
 * sa ligne ne s'affiche jamais, même quand une valeur traîne dans l'étude.
 */
function postesDuSegment(etude: OptimTurpeV2Etude): OptimTurpeV2PlageKey[] {
    return etude.compteur?.segment === "C4"
        ? PLAGE_ORDER.filter((p) => p !== "PTE")
        : PLAGE_ORDER
}

/** Les lignes du tableau, dans l'ordre des plages ; vide si rien n'est lisible. */
export function lignesTableauPlages(
    etude: OptimTurpeV2Etude,
    options: OptionsDuGraphique = {}
): LigneTableauPlages[] {
    const actuelle =
        etude.actuelle?.structure_tarifaire?.puissance_souscrite_soutirage
    const colonne = colonneProposee(etude, options)
    const proposee =
        colonne === null
            ? undefined
            : colonne === "Vos puissances"
              ? structureSaisie(etude)
              : etude.meilleure?.structure_tarifaire
                    ?.puissance_souscrite_soutirage
    const pointes = (lirePointesMesurees(etude) ?? {}) as Record<
        string,
        unknown
    >

    if (etude.compteur?.segment === "C5") {
        // Le moteur sert `{ unique: … }` en C5 ; à défaut, la plus haute des
        // pointes servies (la puissance unique couvre toutes les heures).
        const servies = Object.values(pointes)
            .map(fini)
            .filter((v): v is number => v !== null)
        const ligne: LigneTableauPlages = {
            cle: "unique",
            libelle: LIBELLE_LIGNE_UNIQUE,
            aujourdhui: fini(actuelle?.puissance),
            pointe:
                fini(pointes.unique) ??
                (servies.length ? Math.max(...servies) : null),
            propose: fini(proposee?.puissance),
        }
        return ligne.aujourdhui === null &&
            ligne.pointe === null &&
            ligne.propose === null
            ? []
            : [ligne]
    }

    const denA: Partial<Record<OptimTurpeV2PlageKey, number>> =
        actuelle?.denivele ?? {}
    const denP: Partial<Record<OptimTurpeV2PlageKey, number>> =
        proposee?.denivele ?? {}
    return postesDuSegment(etude)
        .filter(
            (p) =>
                fini(denA[p]) !== null ||
                fini(denP[p]) !== null ||
                fini(pointes[p]) !== null
        )
        .map((p) => ({
            cle: p,
            libelle: majuscule(libelleClasseHoraire(p)),
            aujourdhui: fini(denA[p]),
            pointe: fini(pointes[p]),
            propose: fini(denP[p]),
        }))
}

/**
 * La puissance recommandée poste par poste, TOUJOURS dite (revue du
 * 19/10/2026, retours N1 et N2) : le graphique dessine deux barres par poste
 * et le tableau « Voir les valeurs » garde sa colonne, même quand rien ne
 * change.
 * - une colonne nommée par `colonneProposee` garde ses valeurs (« Recommandé »,
 *   « Vos puissances », « Option chiffrée ») ;
 * - un C5 dont la réserve est servie recommande la puissance adaptée de la
 *   réserve, à défaut celle de `meilleure` (DEV-2423), même sous un surcoût ;
 * - « Rien à changer » (structure actuelle, option sous le seuil, surcoût) :
 *   la recommandée est la puissance d'aujourd'hui, `inchangee` ;
 * - sinon (économie non chiffrée, état inconnu), rien n'est recommandé :
 *   `null`, jamais une recopie qui se lirait comme un conseil.
 */
export type RecommandeeParPoste = {
    /** « Recommandé », « Vos puissances » ou « Option chiffrée ». */
    colonne: string
    /** La valeur par clé de ligne (`HPH`…, `unique`), `null` quand elle manque. */
    valeurs: Record<string, number | null>
    /** Vrai quand la recommandée recopie la puissance d'aujourd'hui. */
    inchangee: boolean
}

export function recommandeeParPoste(
    etude: OptimTurpeV2Etude,
    options: OptionsDuGraphique = {}
): RecommandeeParPoste | null {
    const lignes = lignesTableauPlages(etude, options)
    if (!lignes.length) return null
    const colonne = colonneProposee(etude, options)
    if (colonne !== null) {
        const valeurs = Object.fromEntries(
            lignes.map((l) => [l.cle, l.propose])
        )
        return {
            colonne,
            valeurs,
            inchangee:
                colonne === "Recommandé" &&
                lignes.every(
                    (l) => l.propose !== null && l.propose === l.aujourdhui
                ),
        }
    }
    const chiffre = lireChiffre(etude)
    if (chiffre.estScenarioLibre) return null
    if (
        estC5(etude) &&
        reserveTitreC5(etude) !== null &&
        !options.optionChiffree
    ) {
        // La puissance qui lève le risque, celle de la réserve et de la
        // demande (US12, D9) ; celle de `meilleure` seulement quand la réserve
        // ne la sert pas (sous le chiffrage dédié, DEV-2422, `meilleure` est
        // la combinaison retenue par l'optimisation, pas la puissance adaptée).
        const unique =
            montantC5(etude)?.puissanceKva ??
            fini(
                etude.meilleure?.structure_tarifaire
                    ?.puissance_souscrite_soutirage?.puissance
            )
        if (unique !== null)
            return {
                colonne: "Recommandé",
                valeurs: { unique },
                inchangee: false,
            }
    }
    if (chiffre.montantServi === false || chiffre.etat === "montant_non_servi")
        return null
    const statut = lireStatutStructure(etude)
    if (
        statut === "structure_actuelle" ||
        statut === "option_sous_le_seuil" ||
        statut === "surcout"
    )
        return {
            colonne: "Recommandé",
            valeurs: Object.fromEntries(
                lignes.map((l) => [l.cle, l.aujourdhui])
            ),
            inchangee: true,
        }
    return null
}

/** L'entête de la colonne recommandée : « Recommandée », « Vos puissances »… */
export function enteteColonneRecommandee(colonne: string): string {
    return colonne === "Recommandé" ? "Recommandée" : colonne
}

/**
 * « courte utilisation » : le libellé en clair, sans le mot « formule » ni le
 * code Enedis, réservé à la demande au fournisseur et au rapport (Q2).
 */
function formuleLue(code: string): string {
    return libelleFormuleTarifaireSansCode(code).replace(/^formule /, "")
}

/**
 * La formule avant → après : « longue utilisation (HTALU5) → courte
 * utilisation (HTACU5) », ou « courte utilisation (BTSUPCU4) → inchangée ».
 * Sous une structure non recommandée, la formule reste celle d'aujourd'hui.
 * `null` sans aucune formule lue.
 */
export function formuleAvantApres(
    etude: OptimTurpeV2Etude
): { texte: string; change: boolean } | null {
    const { avant, apres, changed } = getFtaChange(etude)
    // Q21 : sous une économie non chiffrée, rien n'est recommandé ; la
    // formule actuelle se lit seule.
    const chiffre = lireChiffre(etude)
    if (
        !chiffre.estScenarioLibre &&
        (chiffre.montantServi === false || chiffre.etat === "montant_non_servi")
    ) {
        return avant ? { texte: formuleLue(avant), change: false } : null
    }
    const lue = proposeeLue(etude)
    if (lue && changed && avant && apres) {
        return {
            texte: `${formuleLue(avant)} → ${formuleLue(apres)}`,
            change: true,
        }
    }
    const fta = avant ?? (lue ? apres : undefined)
    return fta
        ? { texte: `${formuleLue(fta)} → inchangée`, change: false }
        : null
}

// =============================================================================
// Le graphique des puissances souscrites (série UX du témoin, 15/10/2026, 2.1).
//
// Ce que le courtier doit vivre : en cinq secondes, pour chaque poste, ce
// qu'il paie aujourd'hui, ce que l'étude propose, ce qui a été réellement
// appelé et où se trouvent les dépassements. Les chiffres restent dans les
// colonnes ; le graphique en montre les relations. Toutes les barres partent
// de zéro sur une même échelle visible, et un signe porte une seule grandeur.
//
// Les fonctions ci-dessous sont pures : le composant ne fait que dessiner.
// =============================================================================

/** Marge de l'échelle au-delà de la plus grande valeur (2.1 point 1). */
export const MARGE_ECHELLE = 1.04

/** Le raccordement se dessine quand il tient sous 1,25 fois l'échelle (point 7). */
export const RACCORDEMENT_PROCHE = 1.25

/**
 * La marge de sécurité que le moteur a appliquée à un C4 ou un C2, en points
 * de pourcentage, pour juger un poste « relevé ». Le moteur lit
 * `optimisation.marge_securite_pct` et prend 0 quand elle est absente
 * (`denivele_avec_marge(…, nested_get(…) or 0.0)`, courtage) : absente, elle
 * vaut donc 0 ici. Ambiguë, elle n'est pas connue et rien n'est dit.
 */
function margeMoteurPct(etude: OptimTurpeV2Etude): number | null {
    const brute = etude.inputs?.optimisation?.marge_securite_pct
    if (typeof brute !== "number" || !Number.isFinite(brute)) return 0
    const lue = relireValeurServie(brute)
    return lue.ambigue ? null : lue.pct
}

/**
 * Le besoin propre d'un poste tel que le moteur le calcule : la puissance
 * maximale atteinte arrondie au kVA supérieur, augmentée de la marge, puis
 * arrondie au kVA supérieur (`find_best_fta_denivele_C4_C2_no_cmpds`,
 * `marge_securite_test`).
 */
function besoinMoteur(pointe: number, margePct: number): number {
    return Math.ceil(Math.ceil(pointe) * (1 + margePct / 100) - 1e-9)
}

/**
 * Seuil de LISIBILITÉ, pas règle métier : la mention ne s'écrit que lorsque
 * la barre proposée se voit nettement plus longue que la puissance maximale
 * atteinte (au moins 25 % au-dessus). Il ne fait jamais dire « relevée » à
 * un poste que la croissance n'a pas relevé ; il tait seulement un relèvement
 * de quelques kVA que le graphique ne montre pas (C2 rejoué : 341 porté à 347).
 */
export const RELEVEE_ECART_LISIBLE = 1.25

/** La nature d'une zone hachurée : elle fixe sa couleur et sa mention. */
export type NatureDepassement = "attendu" | "contrat_actuel" | "disjonction"

/** La mention en bout de ligne, par nature (points 6 et 10). */
export const MENTION_DEPASSEMENT: Record<NatureDepassement, string> = {
    attendu: "dépassements attendus",
    contrat_actuel: "dépassements sur le contrat actuel",
    disjonction: "risque de disjonction",
}

/** Une ligne du graphique : les trois valeurs et les zones qui en découlent. */
export type SegmentsLigne = LigneTableauPlages & {
    /**
     * L'intervalle hachuré entre la puissance souscrite (proposée, ou
     * actuelle sans colonne proposée) et la puissance maximale atteinte, posé
     * sur la barre dont il dépasse. `null` sans dépassement.
     */
    depassement: {
        de: number
        a: number
        nature: NatureDepassement
        barre: "aujourdhui" | "propose"
    } | null
    /**
     * La bande de marge de sécurité, de la puissance maximale atteinte à
     * cette puissance augmentée de la marge, sur le seul poste qui fixe la
     * puissance (point 5). `null` quand la marge n'est pas lisible.
     */
    marge: { de: number; a: number; pct: number } | null
    /** La barre proposée porte la mention « relevée » (point 8). */
    relevee: boolean
}

/**
 * La marge de sécurité appliquée, en points de pourcentage, ou `null` quand
 * elle n'est pas lisible.
 * - C5 : celle que le moteur sert sous `pmax_mesuree.marge_appliquee`
 *   (fraction). Non servie, la bande se tait plutôt que d'affirmer un 15 %
 *   non prouvé (même règle que `libelleMargeC5Servie`).
 * - C4 et C2 : `inputs.optimisation.marge_securite_pct`, relue par
 *   `relireValeurServie` ; une valeur absente ou ambiguë n'est pas lisible.
 * - Étude saisie : aucune marge, les puissances sont imposées.
 */
export function margeLisiblePct(etude: OptimTurpeV2Etude): number | null {
    if (lireChiffre(etude).estScenarioLibre) return null
    if (estC5(etude)) {
        const servie = etude.pmax_mesuree?.marge_appliquee
        if (typeof servie !== "number" || !Number.isFinite(servie)) return null
        const pct = Math.round(servie * 1000) / 10
        return pct > 0 ? pct : null
    }
    const lue = relireValeurServie(
        etude.inputs?.optimisation?.marge_securite_pct
    )
    if (lue.lecture === "defaut" || lue.ambigue) return null
    return lue.pct > 0 ? lue.pct : null
}

/** Arrondi à deux décimales, pour les intervalles affichés. */
function deuxDecimales(v: number): number {
    return Math.round(v * 100) / 100
}

/**
 * Les lignes du graphique, dans l'ordre du tableau. Une valeur absente n'a ni
 * barre ni signe, jamais 0 : elle reste `null`.
 */
export function segmentsTableauPlages(
    etude: OptimTurpeV2Etude,
    options: OptionsDuGraphique = {}
): SegmentsLigne[] {
    const colonne = colonneProposee(etude, options)
    const c5 = estC5(etude)
    const saisie = lireChiffre(etude).estScenarioLibre
    const reserveC5 = c5 && !saisie && reserveTitreC5(etude) !== null
    const margePct = margeLisiblePct(etude)
    const motrice = c5 ? "unique" : getDrivingPlage(etude)
    const lignes = lignesTableauPlages(etude, options)
    const margeMoteur = c5 || saisie ? null : margeMoteurPct(etude)

    return lignes.map((ligne, i) => {
        // Q51 : une étude saisie C5 ne dit rien de la puissance maximale
        // atteinte sur le graphique.
        const pointe = c5 && saisie ? null : ligne.pointe

        let depassement: SegmentsLigne["depassement"] = null
        if (pointe !== null) {
            if (c5) {
                // Q22 : sur un C5, la puissance maximale atteinte au-dessus de
                // la souscrite fait disjoncter ; la zone ne se dessine que si
                // le moteur sert la réserve, en rouge.
                if (
                    reserveC5 &&
                    ligne.aujourdhui !== null &&
                    ligne.aujourdhui < pointe
                ) {
                    depassement = {
                        de: ligne.aujourdhui,
                        a: pointe,
                        nature: "disjonction",
                        barre: "aujourdhui",
                    }
                }
            } else if (colonne !== null) {
                if (ligne.propose !== null && ligne.propose < pointe) {
                    depassement = {
                        de: ligne.propose,
                        a: pointe,
                        nature: "attendu",
                        barre: "propose",
                    }
                }
            } else if (ligne.aujourdhui !== null && ligne.aujourdhui < pointe) {
                depassement = {
                    de: ligne.aujourdhui,
                    a: pointe,
                    nature: "contrat_actuel",
                    barre: "aujourdhui",
                }
            }
        }

        const marge =
            margePct !== null && pointe !== null && ligne.cle === motrice
                ? {
                      de: pointe,
                      a: deuxDecimales(pointe * (1 + margePct / 100)),
                      pct: margePct,
                  }
                : null

        // Point 8, lu strictement sur la croissance des postes : le poste
        // reprend exactement la puissance proposée du poste qui le précède,
        // et cette puissance dépasse son besoin propre. Le poste qui fixe la
        // puissance n'est jamais relevé. Toute autre cause d'écart (borne de
        // segment, choix de l'optimiseur) ne porte pas la mention, et un
        // relèvement que la barre ne montre pas se tait.
        const precedent = lignes
            .slice(0, i)
            .reverse()
            .find((l) => l.propose !== null)
        const relevee =
            !c5 &&
            (colonne === "Recommandé" || colonne === COLONNE_OPTION_CHIFFREE) &&
            margeMoteur !== null &&
            ligne.cle !== motrice &&
            ligne.propose !== null &&
            pointe !== null &&
            precedent !== undefined &&
            precedent.propose === ligne.propose &&
            ligne.propose > besoinMoteur(pointe, margeMoteur) &&
            ligne.propose >= pointe * RELEVEE_ECART_LISIBLE

        return { ...ligne, pointe, depassement, marge, relevee }
    })
}

/**
 * L'info-bulle de la mention « relevée » (revue 2 en production, I39) : le
 * mot seul n'est pas compris d'un client. Le premier poste est celui du
 * segment : HPH en C4, la pointe en C2.
 */
export function infoRelevee(segment: string | null | undefined): string | null {
    // Relecture sceptique de la revue 2 : sans segment connu, l'écran ne
    // devine pas le premier poste ; l'info-bulle se tait.
    if (segment !== "C4" && segment !== "C2") return null
    const debut =
        segment === "C4"
            ? "des heures pleines de saison haute"
            : "des heures de pointe"
    return `Puissance alignée sur le poste précédent : les puissances souscrites doivent croître ${debut} aux heures creuses de saison basse.`
}

/** Le raccordement tel que le graphique le montre, ou `null` s'il est inconnu. */
export type RaccordementGraphique = {
    valeur: number
    unite: string
    /** Vrai : trait pointillé sur l'échelle ; faux : une ligne sous l'axe. */
    proche: boolean
}

/** La plus grande valeur dessinée, sans marge ni raccordement. */
function plusGrandeValeur(lignes: SegmentsLigne[]): number | null {
    const valeurs = lignes.flatMap((l) =>
        [l.aujourdhui, l.pointe, l.propose].filter(
            (v): v is number => v !== null && v > 0
        )
    )
    return valeurs.length ? Math.max(...valeurs) : null
}

/**
 * Le raccordement du graphique (point 7) : proche quand il tient sous
 * 1,25 fois l'échelle des valeurs, dans l'unité du tableau. Une unité qui
 * diffère de celle du tableau ne se dessine jamais sur l'échelle.
 */
export function raccordementDuGraphique(
    etude: OptimTurpeV2Etude,
    lignes: SegmentsLigne[] = segmentsTableauPlages(etude)
): RaccordementGraphique | null {
    const lu = lireRaccordement(etude)
    if (!lu || lu.valeur === null || lu.valeur <= 0) return null
    const unite = lu.unite ?? getUnitePuissance(etude)
    const max = plusGrandeValeur(lignes)
    const proche =
        max !== null &&
        unite === getUnitePuissance(etude) &&
        lu.valeur <= max * MARGE_ECHELLE * RACCORDEMENT_PROCHE
    return { valeur: lu.valeur, unite, proche }
}

/**
 * Le maximum de l'échelle commune (point 1) : la plus grande valeur dessinée
 * augmentée de 4 %, étendue au raccordement quand il est proche pour que son
 * trait reste dans le cadre. `null` quand rien n'est dessinable.
 */
export function echelleTableauPlages(
    lignes: SegmentsLigne[],
    raccordement: RaccordementGraphique | null = null
): number | null {
    const max = plusGrandeValeur(lignes)
    if (max === null) return null
    const racc = raccordement?.proche ? raccordement.valeur : 0
    return Math.max(max, racc) * MARGE_ECHELLE
}

/**
 * Les graduations de l'axe (point 1) : 0 puis trois graduations rondes au
 * plus, au pas de 1, 2 ou 5 fois une puissance de dix (10, 20 ou 50 sur les
 * échelles usuelles d'un C4), toutes dans l'échelle.
 */
export function graduationsAxe(echelle: number | null): number[] {
    if (echelle === null || !(echelle > 0)) return []
    const puissance = Math.pow(10, Math.floor(Math.log10(echelle / 3)))
    const pas =
        [1, 2, 5, 10]
            .map((f) => f * puissance)
            .find((p) => Math.floor(echelle / p) <= 3) ?? 10 * puissance
    const n = Math.floor(echelle / pas)
    return Array.from({ length: n + 1 }, (_, i) => deuxDecimales(i * pas))
}

/** La position d'une valeur sur l'échelle, en pourcentage borné à [0, 100]. */
export function positionPct(v: number, echelle: number): number {
    return Math.min(100, Math.max(0, (v / echelle) * 100))
}

const NOMBRE_FR = formateurFr({ maximumFractionDigits: 2 })

/** « 67,83 » : la valeur seule, à deux décimales au plus. */
export function nombreFr(v: number): string {
    return NOMBRE_FR.format(v)
}

/**
 * Le texte du survol d'une ligne (point 9) : « HPB : aujourd'hui 66 kVA ·
 * recommandé 62 kVA · puissance maximale atteinte 67,83 kVA · dépassement
 * 5,83 kVA ». Le graphique reste masqué aux lecteurs d'écran : le tableau
 * porte les mêmes chiffres.
 */
export function survolLigne(
    ligne: SegmentsLigne,
    colonne: string | null,
    unite: string
): string {
    const v = (x: number) => `${nombreFr(x)}\u00a0${unite}`
    const nom = ligne.cle === "unique" ? ligne.libelle : ligne.cle
    const parties: string[] = []
    if (ligne.aujourdhui !== null)
        parties.push(`aujourd'hui ${v(ligne.aujourdhui)}`)
    if (colonne && ligne.propose !== null)
        parties.push(`${colonne.toLowerCase()} ${v(ligne.propose)}`)
    if (ligne.pointe !== null)
        parties.push(`puissance maximale atteinte ${v(ligne.pointe)}`)
    if (ligne.depassement?.nature === "disjonction")
        parties.push(MENTION_DEPASSEMENT.disjonction)
    else if (ligne.depassement)
        parties.push(
            `dépassement ${v(deuxDecimales(ligne.depassement.a - ligne.depassement.de))}`
        )
    if (ligne.marge)
        parties.push(`marge de sécurité ${nombreFr(ligne.marge.pct)} %`)
    return `${nom} : ${parties.join(" · ")}`
}

/** Un élément de la légende : son signe et son texte. */
export type ElementLegende = {
    signe:
        | "aujourdhui"
        | "propose"
        | "pointe"
        | "hachures"
        | "hachures_rouges"
        | "marge"
        | "raccordement"
    texte: string
}

/**
 * La légende en une ligne (point 12), limitée aux signes réellement dessinés :
 * « Barre claire : puissance souscrite aujourd'hui · barre pleine : recommandé
 * · trait : puissance maximale atteinte · hachures : dépassements ».
 */
export function legendeGraphique(
    lignes: SegmentsLigne[],
    colonne: string | null,
    raccordement: RaccordementGraphique | null
): ElementLegende[] {
    const elements: ElementLegende[] = []
    if (lignes.some((l) => l.aujourdhui !== null))
        elements.push({
            signe: "aujourdhui",
            texte: "barre claire : puissance actuelle",
        })
    if (colonne && lignes.some((l) => l.propose !== null))
        elements.push({
            signe: "propose",
            texte: `barre pleine : ${colonne.toLowerCase()}`,
        })
    if (lignes.some((l) => l.pointe !== null))
        elements.push({
            signe: "pointe",
            texte: "trait : puissance maximale atteinte",
        })
    if (lignes.some((l) => l.marge))
        elements.push({
            signe: "marge",
            texte: "bleu clair : marge de sécurité",
        })
    if (
        lignes.some(
            (l) => l.depassement && l.depassement.nature !== "disjonction"
        )
    )
        elements.push({ signe: "hachures", texte: "rouge : dépassement" })
    if (lignes.some((l) => l.depassement?.nature === "disjonction"))
        elements.push({
            signe: "hachures_rouges",
            texte: "rouge : risque de disjonction",
        })
    if (raccordement?.proche)
        elements.push({
            signe: "raccordement",
            texte: "pointillé : raccordement",
        })
    if (elements.length)
        elements[0] = {
            ...elements[0],
            texte: majuscule(elements[0].texte),
        }
    return elements
}

/**
 * Maquette finale du 15/10/2026 (DEV-2404) : les lignes de « Pourquoi ces
 * puissances », sorties de la conclusion et du pied du graphique : la formule
 * avant → après (M2 : « Formule d'acheminement (FTA) »), le raccordement hors
 * échelle, la règle de croissance quand un poste est relevé. Une ligne sans
 * valeur servie n'est pas écrite.
 */
export function lignesDuPourquoi(
    etude: OptimTurpeV2Etude,
    regleDeCroissance: string | null
): {
    libelle: string
    texte: string
    change: boolean
    testid: string
    titre?: string
}[] {
    const lignes: {
        libelle: string
        texte: string
        change: boolean
        testid: string
        titre?: string
    }[] = []
    const formule = formuleAvantApres(etude)
    if (formule)
        lignes.push({
            libelle: "Formule d'acheminement (FTA)",
            texte: formule.texte,
            change: formule.change,
            testid: "optim-turpe-v2-ligne-formule",
        })
    const segments = segmentsTableauPlages(etude)
    const raccordement = raccordementDuGraphique(etude, segments)
    if (raccordement && !raccordement.proche)
        lignes.push({
            libelle: "Raccordement",
            texte: `${nombreFr(raccordement.valeur)}\u00a0${raccordement.unite}, hors échelle du graphique`,
            change: false,
            testid: "optim-turpe-v2-ligne-raccordement",
        })
    // Revue 2 en production (B3, I14, I37) : une valeur d'une ligne ; la
    // règle entière en info-bulle (et dans la preuve, sous le graphique).
    // Relecture sceptique de la revue 2 : les bornes sont les postes servis
    // (premier et dernier du tableau), jamais devinées d'après le segment ;
    // sans segment connu, la ligne se tait.
    const segment = etude.compteur?.segment
    const premier = segments[0]?.cle
    const dernier = segments[segments.length - 1]?.cle
    if (
        regleDeCroissance &&
        (segment === "C4" || segment === "C2") &&
        premier &&
        dernier &&
        premier !== dernier &&
        segments.some((l) => l.relevee)
    )
        lignes.push({
            libelle: "Postes relevés",
            texte: `alignés sur le poste précédent, de ${premier} à ${dernier}`,
            titre: regleDeCroissance,
            change: false,
            testid: "optim-turpe-v2-ligne-relevee",
        })
    return lignes
}

/**
 * Les puces de « Pourquoi ces puissances » (revue 2 en production, B3, I14,
 * I37), trois au plus, une ligne chacune, composées des seules valeurs
 * servies du tableau, jamais d'une phrase devinée :
 * - la pointe : la plus forte pointe mesurée et son poste, en clair ;
 * - les postes alignés : ceux que la règle de croissance relève ;
 * - la marge : la marge de sécurité servie, quand elle est lisible et non
 *   nulle.
 * Une puce sans valeur servie n'est pas écrite ; le paragraphe complet du
 * moteur reste derrière « Voir le raisonnement complet ».
 */
export function pucesDuPourquoi(etude: OptimTurpeV2Etude): string[] {
    const segment = etude.compteur?.segment
    if (segment !== "C4" && segment !== "C2") return []
    const lignes = segmentsTableauPlages(etude)
    const unite = getUnitePuissance(etude)
    const puces: string[] = []
    const pic = lignes
        .filter((l) => l.pointe !== null && l.pointe > 0)
        .sort((a, b) => (b.pointe ?? 0) - (a.pointe ?? 0))[0]
    if (pic && pic.pointe !== null)
        puces.push(
            `Pointe mesurée : ${nombreFr(pic.pointe)}\u00a0${unite} en ${LIBELLE_CLASSE_HORAIRE[pic.cle] ?? pic.cle}${LIBELLE_CLASSE_HORAIRE[pic.cle] ? ` (${pic.cle})` : ""}.`
        )
    const relevees = lignes.filter((l) => l.relevee).map((l) => l.cle)
    if (relevees.length)
        puces.push(
            `Alignés sur le poste précédent : ${relevees.length} poste${relevees.length > 1 ? "s" : ""} (${relevees.join(", ")}).`
        )
    const brute = etude.inputs?.optimisation?.marge_securite_pct
    if (typeof brute === "number" && Number.isFinite(brute)) {
        const lue = relireValeurServie(brute)
        if (!lue.ambigue && lue.pct > 0)
            puces.push(
                `Marge de sécurité : ${nombreFr(lue.pct)}\u00a0% au-dessus de la pointe mesurée de chaque poste.`
            )
    }
    return puces
}

/**
 * L'encadré « Option chiffrée » sous le graphique (revue 2 en production,
 * I61, I87 ; DEV-2404), écrit par le front, jamais la phrase du moteur :
 * « Cette option ferait gagner 21 € HT par an, sous le seuil de 50 € : aucune
 * démarche nécessaire. »
 * - toujours « par an » quand la période est une année, ou quand le moteur
 *   sert l'équivalent annuel ; sinon l'unité de la période (Q15) ;
 * - le seuil se compare au montant ARRONDI affiché : « 50 € … sous le seuil
 *   de 50 € » se contredisait ; à égalité, « au niveau du seuil de 50 € » ;
 * - un équivalent annuel arrondi au-dessus du seuil (le moteur compare le
 *   montant de la période) le dit, au lieu d'écrire « sous le seuil ».
 * `null` sans montant lisible.
 */
export function phraseOptionChiffree(etude: OptimTurpeV2Etude): string | null {
    const chiffre = lireChiffre(etude)
    const tete = montantDeTete(chiffre)
    if (!tete) return null
    const annuel =
        tete.unite === "par an"
            ? tete.montantEur
            : fini(chiffre.equivalentAnnuel?.nette_eur)
    const montant = annuel ?? tete.montantEur
    const unite = annuel !== null ? "par an" : tete.unite
    const arrondi = Math.round(Math.abs(montant))
    const fin = "aucune démarche nécessaire."
    if (arrondi === 0) return `Cette option ne changerait pas le coût : ${fin}`
    const somme = `${nombreFr(arrondi)} € HT`
    if (montant < 0)
        return `Cette option coûterait ${somme} de plus ${unite} : ${fin}`
    const euros = `${somme} ${unite}`
    const seuil = fini(chiffre.seuilEur)
    if (seuil === null) return `Cette option ferait gagner ${euros} : ${fin}`
    const seuilFr = `${nombreFr(seuil)} €`
    if (arrondi < seuil)
        return `Cette option ferait gagner ${euros}, sous le seuil de ${seuilFr} : ${fin}`
    if (arrondi === seuil)
        return `Cette option ferait gagner ${euros}, au niveau du seuil de ${seuilFr} : ${fin}`
    return `Cette option ferait gagner ${euros} ; sur la période étudiée, l'écart reste sous le seuil de ${seuilFr} : ${fin}`
}
